/**
 * Login em duas etapas.
 *
 * Etapa 1: e-mail e senha. Etapa 2: o código de seis dígitos que chegou por
 * e-mail e por WhatsApp. Nenhuma sessão existe entre as duas.
 *
 * Um único botão âmbar por tela (manual §4). "Voltar" e "Esqueci minha senha" são
 * fantasma/secundário — se disputassem o âmbar, ele deixaria de significar
 * "a ação principal desta tela".
 */
import { ArrowLeft, Mail, MessageCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AuthLayout } from '@/components/AuthLayout';
import { CampoSenha } from '@/components/CampoSenha';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/misc';
import { api, type Desafio, type Usuario } from '@/lib/api';
import { useSessao } from '@/lib/auth';

function Contagem({ ate }: { ate: string }) {
  const [restante, setRestante] = useState(() => Math.max(0, +new Date(ate) - Date.now()));
  useEffect(() => {
    const t = setInterval(() => setRestante(Math.max(0, +new Date(ate) - Date.now())), 1000);
    return () => clearInterval(t);
  }, [ate]);
  const seg = Math.floor(restante / 1000);
  if (seg === 0) return <span className="text-danger">O código expirou. Peça outro.</span>;
  return (
    <span className="tipo-dado text-text-tertiary">
      expira em {String(Math.floor(seg / 60)).padStart(2, '0')}:{String(seg % 60).padStart(2, '0')}
    </span>
  );
}

export function Login() {
  const navegar = useNavigate();
  const { recarregar } = useSessao();

  const [desafio, setDesafio] = useState<Desafio | null>(null);
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [codigo, setCodigo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const campoCodigo = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (desafio) campoCodigo.current?.focus();
  }, [desafio]);

  async function enviarSenha(e: React.FormEvent) {
    e.preventDefault();
    setEnviando(true);
    const r = await api.post<Desafio>('/auth/login', { email, senha });
    setEnviando(false);
    if (!r.ok) {
      toast.error(r.erro);
      return;
    }
    setDesafio(r.dados);
  }

  async function enviarCodigo(e: React.FormEvent) {
    e.preventDefault();
    if (!desafio) return;
    setEnviando(true);
    const r = await api.post<Usuario>('/auth/verificar', {
      desafio_id: desafio.desafio_id,
      codigo,
    });
    setEnviando(false);
    if (!r.ok) {
      toast.error(r.erro);
      setCodigo('');
      campoCodigo.current?.focus();
      return;
    }
    await recarregar();
    navegar(r.dados.deve_trocar_senha ? '/trocar-senha' : '/', { replace: true });
  }

  if (!desafio) {
    return (
      <AuthLayout subtitulo="Inteligência de identidade e correlação de dados">
        <form onSubmit={enviarSenha} className="rounded-lg border border-border bg-surface p-6">
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-2">
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                required
                className="font-mono"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <CampoSenha
              id="senha"
              rotulo="Senha"
              autoComplete="current-password"
              valor={senha}
              onChange={setSenha}
            />
            <Button type="submit" variant="primario" className="w-full" disabled={enviando}>
              {enviando ? <Spinner /> : 'Entrar'}
            </Button>
            <Link
              to="/esqueci"
              className="text-center text-[13px] text-accent outline-none hover:text-accent-hover focus-visible:shadow-[var(--focus-ring)]"
            >
              Esqueci minha senha
            </Link>
          </div>
        </form>
      </AuthLayout>
    );
  }

  const porEmail = desafio.canais.includes('email');
  const porWhats = desafio.canais.includes('whatsapp');

  return (
    <AuthLayout subtitulo="Confirme que é você">
      <form onSubmit={enviarCodigo} className="rounded-lg border border-border bg-surface p-6">
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-2 text-[13px] text-text-secondary">
            <span>Enviamos um código de seis dígitos para:</span>
            <div className="flex flex-col gap-1.5">
              {porEmail && (
                <span className="flex items-center gap-2 text-text">
                  <Mail className="size-4 text-accent" /> seu e-mail
                </span>
              )}
              {porWhats && (
                <span className="flex items-center gap-2 text-text">
                  <MessageCircle className="size-4 text-accent" /> seu WhatsApp
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="codigo">Código</Label>
            <Input
              id="codigo"
              ref={campoCodigo}
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{6}"
              maxLength={6}
              required
              placeholder="000000"
              // O código é um valor estruturado: mono, tabular, espaçado.
              className="h-14 text-center font-mono text-2xl tracking-[0.4em] tabular-nums"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
            />
            <p className="tipo-corpo-pequeno">
              <Contagem ate={desafio.expira_em} />
            </p>
          </div>

          <Button
            type="submit"
            variant="primario"
            className="w-full"
            disabled={enviando || codigo.length !== 6}
          >
            {enviando ? <Spinner /> : 'Confirmar'}
          </Button>

          <Button
            type="button"
            variant="fantasma"
            className="w-full"
            onClick={() => {
              setDesafio(null);
              setCodigo('');
              setSenha('');
            }}
          >
            <ArrowLeft /> Voltar
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
}
